import { hasMessengerV2ConcreteRuntime, isMessengerV2ReadOnlyGroup } from './groupReadiness.js';

const DEFAULT_RETRY_AFTER_MS = 30000;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const toFiniteEpoch = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
};

export const resolveMessengerV2RuntimeRecoveryKey = (directive = null) => {
  const conversationId = toStringOrNull(directive?.conversationId ?? directive?.conversation_id);
  const groupId = toStringOrNull(directive?.groupId ?? directive?.group_id);
  const clientId = toStringOrNull(directive?.clientId ?? directive?.client_id) || 'device';
  const generation =
    toFiniteEpoch(directive?.sessionGeneration ?? directive?.session_generation) ?? 'generation';
  const epoch =
    toFiniteEpoch(directive?.authoritativeEpoch ?? directive?.authoritative_epoch) ?? 'epoch';
  const unresolvedServerSeqs = Array.isArray(
    directive?.unresolvedServerSeqs ?? directive?.unresolved_server_seqs,
  )
    ? (directive.unresolvedServerSeqs ?? directive.unresolved_server_seqs)
        .map((value) => toFiniteEpoch(value))
        .filter((value) => value != null)
        .sort((left, right) => left - right)
        .join(',')
    : '';
  const reason = toStringOrNull(directive?.reason) || 'runtime_recovery_required';

  if (!conversationId || !groupId) return null;
  return [conversationId, groupId, clientId, generation, epoch, reason, unresolvedServerSeqs].join(
    ':',
  );
};

const resolveRecoveryStatus = (groupInfo = null) => {
  if (isMessengerV2ReadOnlyGroup(groupInfo)) return 'read_only';
  if (
    groupInfo?.recoveryBlocked === true ||
    groupInfo?.recovery_blocked === true ||
    String(groupInfo?.readinessState ?? groupInfo?.readiness_state ?? '').toLowerCase() ===
      'blocked'
  ) {
    return 'blocked';
  }
  if (hasMessengerV2ConcreteRuntime(groupInfo)) return 'recovered';
  return 'incomplete';
};

/**
 * Owns runtime recovery attempts for a controller instance. Timeline loading
 * only reports a recovery directive; it never mutates MLS runtime state.
 */
export const createMessengerV2RuntimeRecoveryCoordinator = ({
  now = () => Date.now(),
  retryAfterMs = DEFAULT_RETRY_AFTER_MS,
} = {}) => {
  const entries = new Map();

  const request = ({ directive = null, recover = null } = {}) => {
    const key = resolveMessengerV2RuntimeRecoveryKey(directive);
    if (!key || typeof recover !== 'function') {
      return Promise.resolve({ key, status: 'ignored', performed: false, groupInfo: null });
    }

    const existing = entries.get(key);
    if (existing?.promise) return existing.promise;
    if (
      existing?.status === 'recovered' ||
      existing?.status === 'read_only' ||
      existing?.status === 'blocked'
    ) {
      return Promise.resolve({ ...existing, performed: false });
    }
    if (existing?.retryAt != null && existing.retryAt > now()) {
      return Promise.resolve({ ...existing, status: 'cooldown', performed: false });
    }

    const entry = {
      key,
      groupId: toStringOrNull(directive?.groupId ?? directive?.group_id) || null,
      status: 'running',
      performed: true,
      retryAt: null,
      groupInfo: null,
      error: null,
      promise: null,
    };
    entry.promise = Promise.resolve()
      .then(() => recover(directive))
      .then((groupInfo) => {
        entry.status = resolveRecoveryStatus(groupInfo);
        entry.groupInfo = groupInfo || null;
        entry.retryAt = entry.status === 'incomplete' ? now() + retryAfterMs : null;
        return {
          key,
          status: entry.status,
          performed: true,
          retryAt: entry.retryAt,
          groupInfo: entry.groupInfo,
          error: null,
        };
      })
      .catch((error) => {
        entry.status = 'failed';
        entry.error = error;
        entry.retryAt = now() + retryAfterMs;
        return {
          key,
          status: 'failed',
          performed: true,
          retryAt: entry.retryAt,
          groupInfo: null,
          error,
        };
      })
      .finally(() => {
        entry.promise = null;
      });
    entries.set(key, entry);
    return entry.promise;
  };

  return {
    request,
    clear: () => entries.clear(),
    clearGroup: (groupId = null) => {
      const normalizedGroupId = toStringOrNull(groupId);
      if (!normalizedGroupId) return;
      entries.forEach((entry, key) => {
        if (entry?.groupId === normalizedGroupId) entries.delete(key);
      });
    },
  };
};

export default createMessengerV2RuntimeRecoveryCoordinator;
